<template>
  <v-app id="inspire" class="ism-auth-app" light>
    <!-- Background Elements -->
    <div class="ism-auth-bg" :style="backgroundStyle"></div>
    
    <v-main class="ism-auth-main">
      <v-container fluid fill-height class="pa-0">
        <v-row align="center" justify="center" no-gutters style="height: 100vh;">
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" class="d-flex justify-center">
            
            <!-- Auth Card -->
            <div class="ism-auth-card">
              <!-- Logo -->
              <div class="ism-auth-header">
                <img src="/LogoSvg.svg" alt="iSmartSociety" class="ism-auth-logo" />
                <h2 class="ism-auth-title">Welcome Back</h2>
                <p class="ism-auth-subtitle">Sign in to your admin account</p>
              </div>

              <!-- Form -->
              <v-form @submit.prevent="login" class="ism-auth-form">
                <!-- Email -->
                <PremiumInput
                  v-model="user.email"
                  name="email"
                  label="Email Address"
                  placeholder="Enter your email"
                  type="email"
                  v-validate="'required|email'"
                  :error="errors.first('email') || serverSideErrors.email"
                />

                <!-- Password -->
                <PremiumInput
                  v-model="user.password"
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  :type="showPassword ? 'text' : 'password'"
                  v-validate="'required'"
                  :error="errors.first('password') || serverSideErrors.password"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPassword = !showPassword"
                />

                <!-- Options Row -->
                <div class="ism-auth-options">
                  <v-checkbox
                    v-model="remember"
                    label="Remember me"
                    hide-details
                    color="#F26522"
                    class="ma-0 pa-0 ism-checkbox"
                  ></v-checkbox>
                  <router-link to="/superadmin/forgotPassword" class="ism-auth-link">
                    Forgot Password?
                  </router-link>
                </div>

                <!-- Submit -->
                <button type="submit" class="ism-auth-btn" :disabled="loading">
                  <v-progress-circular v-if="loading" indeterminate size="20" color="#fff" class="mr-2"></v-progress-circular>
                  <span v-else>Sign In</span>
                </button>
              </v-form>
            </div>

          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <Alert />
  </v-app>
</template>

<script>
import Alert from "../../Alert.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import "@/assets/style.css";
import { mapMutations } from "vuex";

export default {
  components: {
    Alert,
    PremiumInput,
  },
  data() {
    return {
      showPassword: false,
      user: {
        email: "",
        password: "",
      },
      remember: false,
      loading: false,
      serverSideErrors: {},
    };
  },
  computed: {
    backgroundStyle() {
      return {
        backgroundImage: `url(${process.env.VUE_APP_API_URL}/web_front/bulding.svg)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),
    login() {
      let _self = this;

      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.remember) {
            localStorage.setItem("email", this.user.email);
            localStorage.setItem("password", this.user.password);
            localStorage.setItem("remember", "true");
          } else {
            localStorage.removeItem("email");
            localStorage.removeItem("password");
            localStorage.removeItem("remember");
          }

          _self.loading = true;

          _self.$axios.post("/superadmin/login", this.user).then((response) => {
            let data = response.data;
            _self.loading = false;

            if (data.status) {
              data.user.token = data.token;
              localStorage.setItem("user", JSON.stringify(data.user));
              this.$axios.defaults.headers.common = {
                Authorization: "Bearer " + data.user.token,
              };
              this.$vuetify.theme.themes.light.primary = data.user.primary_color;
              this.$vuetify.theme.themes.dark.primary = data.user.primary_color;
              this.$vuetify.theme.themes.light.secondary = data.user.button_color;
              this.$vuetify.theme.themes.dark.secondary = data.user.button_color;
              
              // Light mode by default for auth pages, but handle the store theme correctly after login.
              this.$router.push({ path: "/superadmin/dashboard" });
            } else {
              if (data.errors) {
                _self.serverSideErrors = {};
                for (let i = 0; i < data.errors.length; i++) {
                  _self.serverSideErrors[data.errors[i].field] = data.errors[i].message;
                }
              } else {
                this.setAlert({
                  showAlert: true,
                  variant: "red accent-3",
                  timeout: "2000",
                  message: data.message,
                });
              }
            }
          });
          return;
        }
      });
    },
  },
  mounted() {
    let email = localStorage.getItem('email');
    let password = localStorage.getItem('password');
    let remember = localStorage.getItem('remember');

    if (email) this.user.email = email;
    if (password) this.user.password = password;
    if (remember === "true") this.remember = true;
  }
};
</script>

<style scoped>
/* ── Force light mode on entire auth page ── */
.ism-auth-app {
  background-color: #f8fafc !important;
}

/* Override dark-mode CSS vars inside auth card */
.ism-auth-card {
  --card-bg:        #ffffff !important;
  --bg-primary:     #f1f5f9 !important;
  --text-primary:   #1e293b !important;
  --text-secondary: #64748b !important;
  --border-color:   rgba(0,0,0,0.09) !important;
  --border-color2:  rgba(0,0,0,0.15) !important;
}

/* Force PremiumInput to always render in light mode inside auth card */
.ism-auth-card ::v-deep .premium-label {
  color: #475569 !important;
}
.ism-auth-card ::v-deep .premium-input .v-input__slot {
  background: #f1f5f9 !important;
}
.ism-auth-card ::v-deep .premium-input fieldset {
  border-color: rgba(0, 0, 0, 0.12) !important;
}
.ism-auth-card ::v-deep .premium-input input {
  color: #1e293b !important;
}
.ism-auth-card ::v-deep .premium-input input::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}
.ism-auth-card ::v-deep .premium-input .v-icon {
  color: #94a3b8 !important;
}
.ism-auth-card ::v-deep .premium-input .v-label {
  color: #64748b !important;
}

/* Autofill override for Auth Card */
.ism-auth-card ::v-deep input:-webkit-autofill,
.ism-auth-card ::v-deep input:-webkit-autofill:hover, 
.ism-auth-card ::v-deep input:-webkit-autofill:focus, 
.ism-auth-card ::v-deep input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #f1f5f9 inset !important;
  -webkit-text-fill-color: #1e293b !important;
}
/* Checkbox label color */
.ism-auth-card ::v-deep .v-label {
  color: #64748b !important;
}

.ism-auth-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
}

.ism-auth-main {
  position: relative;
  z-index: 2;
}

.ism-auth-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0,0,0,0.05);
  padding: 48px 40px;
  border: 1px solid rgba(0,0,0,0.05);
}

.ism-auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.ism-auth-logo {
  height: 48px;
  max-width: 100%;
  margin-bottom: 24px;
  object-fit: contain;
}

.ism-auth-title {
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
  letter-spacing: -0.03em;
}

.ism-auth-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.ism-auth-form {
  display: flex;
  flex-direction: column;
}

.ism-auth-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -10px;
  margin-bottom: 20px;
}

.ism-auth-link {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #F26522;
  text-decoration: none;
  transition: color 0.2s;
}

.ism-auth-link:hover {
  color: #C94F10;
}

/* Remember Me Checkbox */
.ism-checkbox ::v-deep label {
  font-family: 'Inter', sans-serif !important;
  font-size: 13px !important;
  color: #64748b !important;
  font-weight: 600 !important;
  margin-top: 2px !important;
}
.ism-checkbox ::v-deep .v-icon {
  font-size: 20px !important;
  color: #94a3b8 !important;
}
.ism-checkbox.v-input--is-label-active ::v-deep .v-icon,
.ism-checkbox.v-input--is-dirty ::v-deep .v-icon {
  color: #F26522 !important;
}

.ism-auth-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #F26522, #C94F10);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(242, 101, 34, 0.3);
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 8px;
}

.ism-auth-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(242, 101, 34, 0.4);
}

.ism-auth-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

@media (max-width: 600px) {
  .ism-auth-card {
    padding: 40px 24px;
    border-radius: 20px;
    margin: 16px;
  }
}
</style>